// src/services/staffConfirmation/staffConfirmationErrors.ts - what can go wrong
// while confirming with Google, and the words the operator sees for each.
//
// Two kinds of failure, and the difference matters:
// - A LOCAL failure (StaffConfirmationError) happens before the request is sent:
//   the Google window was blocked or closed, 88ID refused the re-sign-in, the
//   staff session ended. Nothing reached the api, so the action can simply be
//   confirmed again.
// - A REFUSAL is the api's answer to a request that carried a confirmation
//   (stale, already used, for a different request). The office asks once more
//   and then stops, so a broken confirmation can never loop.
//
// COPY: every string below is pending the CTO's approval and the ad-compliance
// read. None of them ever repeats a server message.
// Read without importing stepUp.ts, which imports this module.
const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error === "object" && error !== null && "code" in error) {
    const { code } = error as { code?: unknown };
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
};

/** Codes 88ID and the api answer with (the plan's staff error registry). */
export const STAFF_SERVER_CODES = [
  "STAFF_REAUTH_REQUIRED",
  "REAUTH_NOT_FRESH",
  "REAUTH_ACCOUNT_MISMATCH",
  "NOT_STAFF_ACCOUNT",
  "STAFF_ACCOUNT_SUSPENDED",
  "STAFF_NOT_PREAUTHORISED",
  "SESSION_ENDED",
  "CONFIRMATION_INVALID",
  "CONFIRMATION_EXPIRED",
  "CONFIRMATION_MISMATCH",
  "CONFIRMATION_REPLAYED",
  "RATE_LIMITED",
  "FLOW_EXPIRED",
] as const;

/** Codes the office raises itself, before anything is sent. */
export const STAFF_LOCAL_CODES = [
  "POPUP_BLOCKED",
  "POPUP_CLOSED",
  /** 88ID answered 404 (its tenant policy is off) or no 88ID is configured. */
  "CONFIRMATION_UNAVAILABLE",
  /** A multipart or non-JSON body: there is nothing exact to confirm. */
  "BODY_NOT_CONFIRMABLE",
  /** The confirmation for this click was already used by another request. */
  "CONFIRMATION_HANDLE_SPENT",
  /** The operator closed the dialog while the confirmation was under way. */
  "CONFIRMATION_CANCELLED",
  /** Anything else (network, an unknown 88ID answer). */
  "CONFIRMATION_FAILED",
] as const;

export type StaffServerCode = (typeof STAFF_SERVER_CODES)[number];
export type StaffLocalCode = (typeof STAFF_LOCAL_CODES)[number];
export type StaffConfirmationCode = StaffServerCode | StaffLocalCode;

/** A failure before the request left the office. Carries no token. */
export class StaffConfirmationError extends Error {
  readonly code: StaffConfirmationCode;
  readonly status?: number;
  readonly staffConfirmation = "local" as const;

  constructor(code: StaffConfirmationCode, status?: number) {
    super(staffConfirmationCopy(code));
    this.name = "StaffConfirmationError";
    this.code = code;
    this.status = status;
  }
}

export const isStaffConfirmationError = (
  error: unknown
): error is StaffConfirmationError =>
  error instanceof StaffConfirmationError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { staffConfirmation?: unknown }).staffConfirmation === "local");

const isOneOf = <T extends string>(list: readonly T[], code?: string) =>
  !!code && (list as readonly string[]).includes(code);

/** The api asking for a confirmation that was not sent. */
export const STAFF_CONFIRMATION_REQUIRED_CODE = "STAFF_REAUTH_REQUIRED";

export const isStaffConfirmationRequired = (error: unknown): boolean =>
  !isStaffConfirmationError(error) &&
  getErrorCode(error) === STAFF_CONFIRMATION_REQUIRED_CODE;

/** The api refused a request that carried a confirmation. */
const REFUSAL_CODES = [
  "STAFF_REAUTH_REQUIRED",
  "CONFIRMATION_INVALID",
  "CONFIRMATION_EXPIRED",
  "CONFIRMATION_MISMATCH",
  "CONFIRMATION_REPLAYED",
] as const;

export const isStaffConfirmationRefusal = (error: unknown): boolean =>
  !isStaffConfirmationError(error) &&
  isOneOf(REFUSAL_CODES, getErrorCode(error));

/** Any staff-confirmation answer, local or from the api, that has its own copy. */
export const isStaffConfirmationAnswer = (error: unknown): boolean =>
  isStaffConfirmationError(error) ||
  isOneOf(STAFF_SERVER_CODES, getErrorCode(error));

export const isSessionEnded = (error: unknown): boolean =>
  getErrorCode(error) === "SESSION_ENDED";

// ---- Copy (pending the CTO's approval) -------------------------------------

export const CONFIRM_WITH_GOOGLE_TEXT = "Confirm with Google";
export const CONFIRM_WITH_GOOGLE_HINT =
  "Sign in again with Google to confirm this action.";
export const CONFIRMING_WITH_GOOGLE_TEXT =
  "Finish signing in in the Google window.";
export const CONFIRM_AGAIN_TEXT = "Confirm again with Google";
export const CONFIRMATION_STOPPED_TEXT =
  "This action couldn't be confirmed. Close this and start again.";
export const CONFIRMATION_UNKNOWN_TEXT =
  "Couldn't confirm with Google. Try again.";
export const NEXT_CONFIRMATION_TEXT = (remaining: number) =>
  `Done. ${remaining === 1 ? "One more action is" : `${remaining} more actions are`} waiting. Confirm ${
    remaining === 1 ? "it" : "each one"
  } with Google.`;

const COPY: Record<StaffConfirmationCode, string> = {
  STAFF_REAUTH_REQUIRED: CONFIRM_AGAIN_TEXT,
  REAUTH_NOT_FRESH: CONFIRM_AGAIN_TEXT,
  REAUTH_ACCOUNT_MISMATCH:
    "Sign in with the same Google account you are using here",
  NOT_STAFF_ACCOUNT: "Use your 80eight Google account",
  STAFF_ACCOUNT_SUSPENDED: "Your account isn't active",
  STAFF_NOT_PREAUTHORISED:
    "Your 80eight Google account hasn't been set up for the console yet. Ask an admin to invite you.",
  SESSION_ENDED: "Your session ended. Sign in again to carry on.",
  CONFIRMATION_INVALID: CONFIRM_AGAIN_TEXT,
  CONFIRMATION_EXPIRED: CONFIRM_AGAIN_TEXT,
  CONFIRMATION_MISMATCH: CONFIRM_AGAIN_TEXT,
  CONFIRMATION_REPLAYED: "That confirmation was already used. Confirm again.",
  RATE_LIMITED: "Too many confirmations. Try again in a few minutes.",
  FLOW_EXPIRED: CONFIRM_AGAIN_TEXT,
  POPUP_BLOCKED:
    "Your browser blocked the Google window. Allow pop-ups for this site and try again.",
  POPUP_CLOSED:
    "The Google window closed before you confirmed. Nothing was sent.",
  CONFIRMATION_UNAVAILABLE: CONFIRMATION_UNKNOWN_TEXT,
  BODY_NOT_CONFIRMABLE: CONFIRMATION_UNKNOWN_TEXT,
  CONFIRMATION_HANDLE_SPENT: CONFIRMATION_UNKNOWN_TEXT,
  CONFIRMATION_CANCELLED:
    "The Google window closed before you confirmed. Nothing was sent.",
  CONFIRMATION_FAILED: CONFIRMATION_UNKNOWN_TEXT,
};

/** The operator's words for a code; an unknown code gets the generic line. */
export function staffConfirmationCopy(code?: string | null): string {
  return code && code in COPY
    ? COPY[code as StaffConfirmationCode]
    : CONFIRMATION_UNKNOWN_TEXT;
}

/** The words for any error from a confirmation attempt. Never `error.message`. */
export const staffConfirmationErrorText = (error: unknown): string =>
  staffConfirmationCopy(getErrorCode(error));
